import React, { useEffect, useState } from 'react'
import { path } from 'ramda'
import PropTypes from 'prop-types'
import { Form, Formik } from 'formik'
import effects from 'app/effects/effects'
import { Autocomplete } from '@material-ui/lab'
import { useMutation } from '@tanstack/react-query'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import { FormControlLabel, makeStyles, Typography } from '@material-ui/core'

import Map from 'app/components/Map'
import { fileToBase64 } from 'libs/file'
import { mapNumToBool } from 'commons/boolean'
import { useAppName } from 'commons/usePartner'
import { messageAlertError } from 'commons/error'
import { DISTRESS } from 'app/constants/statusColors'
import { CANCEL, CLOSE, INFO, SECONDARY } from 'app/constants/button'

import MessageAlert from 'app/components/MessageAlert'

import CheckBox from 'app/componentV4/CheckBox'
import LabelInput from 'app/componentV4/Labels/LabelInput'
import PanelsWarning from 'app/componentV4/Panels/Warning'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import UploadImage from 'app/componentV4/Input/UploadImage'
import SubmitDialog from 'app/componentV4/Dialog/SubmitDialog'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import PhoneNumberInputV4 from 'app/componentV4/Input/PhoneNumberInputV4'

import useProfile from '../useProfile'
import GooglePlace from './GooglePlace'
import { validationSchema } from './validation'

const useStyles = makeStyles({
  input: {
    '&.MuiOutlinedInput-root': {
      height: '48px !important',
    },
  },
  groupLabel: { fontWeight: 700, color: '#000' },
  bottomBar: {
    boxShadow: 'unset',
    height: '48px',
    top: 'auto',
    bottom: 0,
    padding: '.5rem 2.5rem',
    zIndex: 90,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
  },
})

function PlaceInfoForm({
  initial,
  categories,
  onFetch,
  isOnboard,
  isFetching,
}) {
  const classes = useStyles()
  const appName = useAppName()
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const { updatePlaceInfo, resentVerifyEmail, cancelVerifyEmail } = useProfile()

  const [logo, setLogo] = useState(initial.logo)
  const [confirmWithDraw, setConfirmWithDraw] = useState(false)

  const pendingApproval = useSelector(
    path(['place', 'info', 'pendingApproval']),
  )

  const uploadAvatar = useMutation({
    mutationFn: async (fileBase64) => {
      const response = await effects.apis.uploadProfilePhoto({
        data: { file: fileBase64 },
      })
      return response
    },
    onSuccess: (response) => {
      setLogo(response.url)
    },
    onError: (error) => {
      const msg = error.response?.data
      dispatch(
        MessageAlert.showError({
          message: messageAlertError(msg, formatMessage),
        }),
      )
    },
  })

  const handleUploadAvatar = async (file) => {
    const base64 = await fileToBase64(file)
    uploadAvatar.mutate(base64)
  }

  useEffect(() => {
    if (
      updatePlaceInfo.isSuccess ||
      resentVerifyEmail.isSuccess ||
      cancelVerifyEmail.isSuccess
    )
      onFetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cancelVerifyEmail.isSuccess,
    resentVerifyEmail.isSuccess,
    updatePlaceInfo.isSuccess,
  ])

  useEffect(() => {
    setLogo(initial.logo)
  }, [initial.logo])

  return (
    <div>
      <Formik
        enableReinitialize
        initialValues={{
          name: initial.name || '',
          email: initial.email || '',
          address: initial.address || '',
          latitude: initial.latitude || 0,
          longitude: initial.longitude || 0,
          shortName: initial.shortName || '',
          contactUrl: initial.contactUrl || '',
          category: initial.category || null,
          description: initial.description || '',
          phoneNumber: initial.phoneNumber || '',
          isTranslationEnabled:
            mapNumToBool(initial.isTranslationEnabled) ?? false,
          utcOffsetMinutes: null, // Address nothing change return null
        }}
        validationSchema={validationSchema(formatMessage)}
        onSubmit={async (values) => {
          await updatePlaceInfo.mutate(values)
        }}
      >
        {({
          dirty,
          values,
          errors,
          touched,
          resetForm,
          handleBlur,
          handleChange,
          setFieldValue,
          setFieldTouched,
        }) => {
          return (
            <Form>
              <div
                className={`p-10 overflow-y-scroll ${pendingApproval ? 'h-[calc(100vh-280px)]' : 'max-h-[calc(100vh-230px)]'}`}
              >
                <Typography className="text-16 text-primary font-500 mb-5">
                  {formatMessage({
                    id: 'profile.info',
                  })}
                </Typography>
                {errors.description && (
                  <PanelsWarning
                    type={DISTRESS}
                    className="my-10 flex space-x-3 text-14"
                  >
                    <div className="mx-3">
                      <FormattedMessage
                        id="notificationTimeline.placeProfile.not.visible"
                        values={{ appName }}
                      />
                    </div>
                  </PanelsWarning>
                )}
                <div className="md:flex">
                  {!isOnboard && (
                    <div className="justify-center flex my-10 md:my-0 md:mr-10">
                      <UploadImage
                        src={logo}
                        id={initial?.id}
                        name="logo"
                        onChange={handleUploadAvatar}
                        isLoading={uploadAvatar.isPending}
                        title={formatMessage({
                          id: 'add.place.logo',
                        })}
                      />
                    </div>
                  )}

                  <div className="w-full">
                    <SimpleInput
                      id="profile-info-description"
                      name="description"
                      placeholder={formatMessage({
                        id: 'profile.description.label',
                      })}
                      value={values.description}
                      maxLength={400}
                      inputProps={{
                        maxLength: 400,
                      }}
                      fullWidth
                      multiline
                      rows={5}
                      onChange={handleChange}
                    />
                    <FormControlLabel
                      className="text-primary"
                      control={
                        <CheckBox
                          checked={values.isTranslationEnabled}
                          onChange={(e) =>
                            setFieldValue(
                              'isTranslationEnabled',
                              e.target.checked,
                            )
                          }
                          name={'isTranslationEnabled'}
                        />
                      }
                      label={formatMessage({
                        id: 'translate.description',
                      })}
                    />
                  </div>
                </div>
                <div className="md:flex justify-between md:space-x-10">
                  <LabelInput
                    label={formatMessage({
                      id: 'profile.editingPlaceNameDialog.textField.label',
                    })}
                    required
                  >
                    <SimpleInput
                      id="profile-info-place-name"
                      className={classes.input}
                      name="name"
                      placeholder={formatMessage({
                        id: 'profile.editingPlaceNameDialog.textField.label',
                      })}
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      maxLength={100}
                      inputProps={{
                        maxLength: 100,
                      }}
                    />
                  </LabelInput>
                  <LabelInput
                    label={formatMessage({
                      id: 'profile.category.label',
                    })}
                    required
                  >
                    <Autocomplete
                      id="profile-info-place-category"
                      name="category"
                      options={categories}
                      placeholder={formatMessage({
                        id: 'profile.category.label',
                      })}
                      onChange={(event, newValue) => {
                        setFieldValue('category', newValue ?? initial.category)
                      }}
                      onBlur={handleBlur}
                      getOptionLabel={(option) =>
                        `${option.categoryGroupName}/${option.label}`
                      }
                      groupBy={(option) => option.categoryGroupName}
                      classes={{
                        groupLabel: classes.groupLabel,
                      }}
                      value={values.category}
                      renderOption={(option) => option.label}
                      getOptionSelected={(option, value) => {
                        return option.id === value.id
                      }}
                      renderInput={(params) => (
                        <div ref={params.InputProps.ref} className="w-full">
                          <SimpleInput
                            className={classes.input}
                            id="profile-info-place-category"
                            placeholder={formatMessage({
                              id: 'profile.category.label',
                            })}
                            size="medium"
                            fullWidth
                            error={touched.category && Boolean(errors.category)}
                            helperText={touched.category && errors.category}
                            {...params}
                          />
                        </div>
                      )}
                    />
                  </LabelInput>
                </div>
                <div className="md:flex justify-between md:space-x-10">
                  <LabelInput
                    label={formatMessage({
                      id: 'account.email.label',
                    })}
                    required
                  >
                    <SimpleInput
                      id="profile-info-place-email"
                      className={classes.input}
                      name="email"
                      placeholder={formatMessage({
                        id: 'email.address.label',
                      })}
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </LabelInput>
                  <LabelInput
                    label={formatMessage({
                      id: 'phone.label',
                    })}
                  >
                    <PhoneNumberInputV4
                      value={values.phoneNumber ?? null}
                      id="profile-info-place-phoneNumber"
                      name="phoneNumber"
                      onChange={(number) => {
                        setFieldValue('phoneNumber', number)
                        setFieldTouched('phoneNumber', true, false)
                      }}
                      error={errors.phoneNumber && touched.phoneNumber}
                      helperText={errors.phoneNumber}
                    />
                  </LabelInput>
                </div>
                {initial.newEmail && (
                  <PanelsWarning
                    type={DISTRESS}
                    className="my-5 flex text-14 md:items-center"
                  >
                    <div className="w-full mx-3 md:flex md:items-center justify-between">
                      <div className="break-all mx-3 w-4/6">
                        <FormattedMessage
                          id="profile.contactDetail.warning.email.verification"
                          values={{
                            pendingEmail: initial.newEmail,
                            b: (text) => <span>{text}</span>,
                          }}
                        />
                      </div>
                      <div className="my-3 md:my-0">
                        <OutlinedButtons
                          id="resent-verify-email"
                          color={INFO}
                          className="px-3 py-1 text-12 text-primary-300 mr-5"
                          onClick={() =>
                            resentVerifyEmail.mutate(initial.newEmail)
                          }
                        >
                          <FormattedMessage id="action.resend" />
                        </OutlinedButtons>
                        <OutlinedButtons
                          id="set-confirm-with-draw"
                          color={INFO}
                          className="px-3 py-1 text-12 text-primary-300"
                          onClick={() => setConfirmWithDraw(true)}
                        >
                          <FormattedMessage id="action.withdraw" />
                        </OutlinedButtons>
                      </div>
                    </div>
                  </PanelsWarning>
                )}
                <div className="md:flex justify-between md:space-x-10">
                  <LabelInput
                    label={formatMessage({
                      id: 'profile.contactUrl.label',
                    })}
                  >
                    <SimpleInput
                      id="profile-info-place-contact-url"
                      className={classes.input}
                      name="contactUrl"
                      placeholder={formatMessage({
                        id: 'profile.contactUrl.label',
                      })}
                      value={values.contactUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      error={touched.contactUrl && Boolean(errors.contactUrl)}
                      helperText={touched.contactUrl && errors.contactUrl}
                    />
                  </LabelInput>
                  <LabelInput
                    label={formatMessage({
                      id: 'profile.info.placeSms.textfield.label',
                    })}
                  >
                    <SimpleInput
                      id="profile-info-place-short-name"
                      className={classes.input}
                      name="shortName"
                      placeholder={formatMessage({
                        id: 'profile.info.placeSms.textfield.label',
                      })}
                      value={values.shortName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      error={touched.shortName && Boolean(errors.shortName)}
                      helperText={touched.shortName && errors.shortName}
                    />
                  </LabelInput>
                </div>
                <div>
                  {!isOnboard && (
                    <LabelInput
                      label={formatMessage({
                        id: 'profile.address.label',
                      })}
                      required
                    >
                      <GooglePlace
                        id="profile-info-place-address"
                        name="address"
                        value={values.address}
                        className={classes.input}
                        placeholder={formatMessage({
                          id: 'profile.address.label',
                        })}
                        onChange={(value) => {
                          const isString = typeof value === 'string'

                          setFieldValue(
                            'address',
                            isString ? value : value.address,
                          )
                          setFieldValue(
                            'latitude',
                            isString ? null : value.location.latitude,
                          )
                          setFieldValue(
                            'longitude',
                            isString ? null : value.location.longitude,
                          )
                          setFieldValue(
                            'utcOffsetMinutes',
                            isString ? 0 : value.utcOffsetMinutes,
                          ) // If Address change by Curated set value, If change by manual set 0
                        }}
                        maxLength={199}
                        inputProps={{
                          maxLength: 199,
                        }}
                        onBlur={handleBlur}
                        error={touched.address && Boolean(errors.address)}
                        helperText={touched.address && errors.address}
                      />
                    </LabelInput>
                  )}

                  <div
                    className="relative h-full w-full"
                    style={{ minHeight: 260 }}
                  >
                    <Map
                      center={{
                        lat: values.latitude,
                        lng: values.longitude,
                      }}
                      gestureHandling="none"
                      onReady={(_, map) => {
                        map.setOptions({
                          draggableCursor: 'default',
                          draggingCursor: 'pointer',
                        })
                      }}
                    />
                  </div>
                </div>
              </div>
              <div
                className={
                  'bg-white border-1 border-primary-350 py-5 justify-end flex space-x-10 px-10 w-full'
                }
              >
                <OutlinedButtons
                  color={CLOSE}
                  className="h-fit"
                  onClick={() => resetForm()}
                >
                  {formatMessage({
                    id: 'action.cancel',
                  })}
                </OutlinedButtons>
                <ContainedButton
                  type="submit"
                  color={SECONDARY}
                  className="h-[41.5px]"
                  disabled={!dirty || updatePlaceInfo.isPending || isFetching}
                  isLoading={updatePlaceInfo.isPending}
                >
                  {formatMessage({
                    id: 'subscriberCrm.editGroup.save.label',
                  })}
                </ContainedButton>
              </div>
            </Form>
          )
        }}
      </Formik>
      <SubmitDialog
        id={`dialog-cancel-verification-email-place`}
        open={confirmWithDraw}
        type={CANCEL}
        title={formatMessage({
          id: 'profile.contactDetail.confirmDialog.email.verification.title',
        })}
        description={formatMessage({
          id: 'profile.contactDetail.confirmDialog.email.verification.body',
        })}
        submitText={formatMessage({
          id: 'profile.contactDetail.confirmDialog.email.verification.okButton',
        })}
        onSubmit={async () => {
          await cancelVerifyEmail.mutate()
          setConfirmWithDraw(false)
        }}
        closeText={formatMessage({
          id: 'text.close',
        })}
        onClose={() => setConfirmWithDraw(false)}
        isLoading={cancelVerifyEmail.isPending}
        disabledSubmit={cancelVerifyEmail.isPending}
      />
    </div>
  )
}

PlaceInfoForm.propTypes = {
  onFetch: PropTypes.func,
  isOnboard: PropTypes.bool,
  isFetching: PropTypes.bool,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
      categoryGroupName: PropTypes.string,
    }),
  ),
  initial: PropTypes.shape({
    id: PropTypes.number,
    logo: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    address: PropTypes.string,
    latitude: PropTypes.number,
    newEmail: PropTypes.string,
    longitude: PropTypes.number,
    shortName: PropTypes.string,
    contactUrl: PropTypes.string,
    description: PropTypes.string,
    countryCode: PropTypes.string,
    phoneNumber: PropTypes.string,
    isTranslationEnabled: PropTypes.number,
    category: PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
      categoryGroupName: PropTypes.string,
    }),
  }),
}

export default PlaceInfoForm
